import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { tipsRegistry } from '../tips/registry';
import { shouldShowTip, dismissTip } from '../tips/storage';

/**
 * Hook for managing tip display state
 *
 * @param {string} tipKey - The key of the tip from the registry
 * @param {boolean} isTriggered - Whether the tip should be triggered (e.g., tool is open)
 * @returns {{
 *   isOpen: boolean,
 *   title: string,
 *   markdownBody: string,
 *   close: (doNotShowAgain: boolean) => void
 * }}
 */
export function useTip(tipKey, isTriggered) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const hasTriggeredRef = useRef(false);
  const tip = tipsRegistry[tipKey];

  // When isTriggered becomes true, check if we should show the tip
  useEffect(() => {
    if (!tip) {
      console.warn(`[Tips] Tip with key "${tipKey}" not found in registry`);
      return;
    }

    if (isTriggered && !hasTriggeredRef.current) {
      hasTriggeredRef.current = true;
      const shouldShow = shouldShowTip(tipKey, tip.version);
      if (shouldShow) {
        setIsOpen(true);
      }
    }

    // Reset when trigger becomes false
    if (!isTriggered) {
      hasTriggeredRef.current = false;
    }
  }, [isTriggered, tipKey, tip]);

  // Close handler
  const close = useCallback(
    (doNotShowAgain) => {
      setIsOpen(false);
      if (doNotShowAgain && tip) {
        dismissTip(tipKey, tip.version);
      }
    },
    [tipKey, tip]
  );

  // Get localized content
  const title = tip ? t(tip.titleKey) : '';
  const markdownBody = tip ? t(tip.bodyKey) : '';

  return {
    isOpen,
    title,
    markdownBody,
    close,
  };
}

/**
 * Hook specifically for the main page tip
 * Shows the tip on first visit
 *
 * @returns {{
 *   isOpen: boolean,
 *   title: string,
 *   markdownBody: string,
 *   close: (doNotShowAgain: boolean) => void
 * }}
 */
export function useMainPageTip() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const hasCheckedRef = useRef(false);
  const tip = tipsRegistry.mainPage;

  // Check on mount if we should show the main page tip
  useEffect(() => {
    if (!hasCheckedRef.current && tip) {
      hasCheckedRef.current = true;
      const shouldShow = shouldShowTip('mainPage', tip.version);
      if (shouldShow) {
        // Small delay to let the viewer initialize
        const timeoutId = setTimeout(() => {
          setIsOpen(true);
        }, 500);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [tip]);

  // Close handler
  const close = useCallback(
    (doNotShowAgain) => {
      setIsOpen(false);
      if (doNotShowAgain && tip) {
        dismissTip('mainPage', tip.version);
      }
    },
    [tip]
  );

  // Get localized content
  const title = tip ? t(tip.titleKey) : '';
  const markdownBody = tip ? t(tip.bodyKey) : '';

  return {
    isOpen,
    title,
    markdownBody,
    close,
  };
}

/**
 * Convenience hook that combines useTip with TipsWindow rendering props
 *
 * @param {string} tipKey - The key of the tip from the registry
 * @param {boolean} isTriggered - Whether the tip should be triggered
 * @returns {{
 *   tipsWindowProps: {
 *     isOpen: boolean,
 *     title: string,
 *     markdownBody: string,
 *     onClose: (opts: { doNotShowAgain: boolean }) => void
 *   }
 * }}
 */
export function useTipWithProps(tipKey, isTriggered) {
  const { isOpen, title, markdownBody, close } = useTip(tipKey, isTriggered);

  return {
    tipsWindowProps: {
      isOpen,
      title,
      markdownBody,
      onClose: ({ doNotShowAgain }) => close(doNotShowAgain),
    },
  };
}
